import React, { useEffect, useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Building2, Users, Droplets, Coffee, Sofa, Plus, Minus, ChevronDown } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { BreadcrumbSchema } from '../components/StructuredData';
export function CommercialCleaning() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Form State
  // Step 1
  const [primaryUse, setPrimaryUse] = useState('');
  const [sqFootage, setSqFootage] = useState('');
  const [ceilings, setCeilings] = useState('');
  const [persons, setPersons] = useState('');
  const [frequency, setFrequency] = useState('');
  const [timeOfDay, setTimeOfDay] = useState('');
  const [covid, setCovid] = useState<boolean | null>(null);
  // Step 2
  const [spaces, setSpaces] = useState({
    offices: 0,
    conference: 0,
    bathrooms: 0,
    cafeterias: 0,
    lounging: 0
  });
  const updateSpace = (key: keyof typeof spaces, increment: boolean) => {
    setSpaces(prev => ({
      ...prev,
      [key]: Math.max(0, prev[key] + (increment ? 1 : -1))
    }));
  };
  // Step 3
  const [flooring, setFlooring] = useState({
    wood: '',
    cement: '',
    carpeting: '',
    ceramic: '',
    other: ''
  });
  // Step 4 & 5
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    unit: '',
    city: '',
    postalCode: ''
  });
  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContact({
      ...contact,
      [e.target.name]: e.target.value
    });
  };
  const percentages = ['0%', '10%', '20%', '30%', '40%', '50%', '60%', '70%', '80%', '90%', '100%'];
  return <div className="min-h-screen w-full font-sans bg-white flex flex-col" data-id="element-382">
      <SEOHead title="Commercial Cleaning | Smith & Jengs Cleaning Services" description="Professional commercial cleaning services in Bicester and Oxfordshire. Reliable, efficient, and tailored to your business needs." canonical="https://smithandjengs.com/commercial-cleaning" data-id="element-383" />
      <BreadcrumbSchema items={[{
      name: 'Home',
      url: 'https://smithandjengs.com/'
    }, {
      name: 'Commercial Cleaning',
      url: 'https://smithandjengs.com/commercial-cleaning'
    }]} data-id="element-384" />
      <Navbar data-id="element-385" />

      <main className="flex-1" data-id="element-386">
        {/* 1. Hero Banner */}
        <section className="relative w-full h-[55vh] min-h-[420px] max-h-[600px] overflow-hidden bg-[#1a2744]" data-id="element-387">
          <div className="absolute inset-0" data-id="element-388">
            <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80" alt="Modern clean office space" className="w-full h-full object-cover object-center" data-id="element-389" />
            {/* Dark gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a2744]/90 via-[#1a2744]/50 to-transparent" data-id="element-390" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a2744]/70 via-transparent to-transparent" data-id="element-391" />
          </div>

          <div className="relative z-10 h-full flex items-end px-6 md:px-12 pb-12 pt-32" data-id="element-392">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8
          }} className="max-w-7xl mx-auto w-full" data-id="element-393">
              <h1 className="font-bebas text-white leading-[0.85] tracking-wide" data-id="element-394">
                <span className="text-7xl md:text-[9rem] lg:text-[11rem] block" data-id="element-395">
                  COMMERCIAL
                </span>
                <span className="font-serif-playfair italic font-normal normal-case text-[#e91e8c] text-6xl md:text-[8rem] lg:text-[10rem] block -mt-2 md:-mt-6" data-id="element-396">
                  Cleaning
                </span>
              </h1>
            </motion.div>
          </div>
        </section>

        {/* 2. Description + Image Section */}
        <section className="w-full bg-white py-16 md:py-24 px-6 md:px-12" data-id="element-397">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center" data-id="element-398">
            {/* Left: Text */}
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="flex flex-col lg:max-w-md" data-id="element-399">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2744] mb-6 leading-tight" data-id="element-400">
                Professional cleaning
                <br data-id="element-401" />
                for your business
                <br data-id="element-402" />
                environment.
              </h2>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed" data-id="element-403">
                A clean workspace is essential for productivity and making a
                great impression on clients. We provide comprehensive commercial
                cleaning solutions tailored to your specific business needs,
                ensuring a spotless and hygienic environment for your team.
              </p>
            </motion.div>

            {/* Right: Image */}
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="w-full h-[400px] lg:h-[500px] rounded-sm overflow-hidden" data-id="element-404">
              <img src="https://cdn.magicpatterns.com/uploads/jJ8rqPBAtF8Zv1F3SffbkU/sj-website-commercial-img1.png" alt="Clean modern office interior" className="w-full h-full object-cover" loading="lazy" data-id="element-405" />
            </motion.div>
          </div>
        </section>

        {/* 3. Services List Section */}
        <section className="w-full bg-white pb-16 md:pb-24 px-6 md:px-12" data-id="element-406">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center" data-id="element-407">
            {/* Left: Image */}
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="w-full h-[400px] lg:h-[500px] rounded-sm overflow-hidden order-2 lg:order-1" data-id="element-408">
              <img src="https://cdn.magicpatterns.com/uploads/hsYfHybdP8AFrJp4GWQcRc/sj-website-commercial-img2.png" alt="Clean office space" className="w-full h-full object-cover" loading="lazy" data-id="element-409" />
            </motion.div>

            {/* Right: List */}
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="flex flex-col order-1 lg:order-2" data-id="element-410">
              <ul className="flex flex-col w-full" data-id="element-411">
                {['Daily or weekly office cleaning', 'Sanitization of high-touch areas', 'Restroom cleaning and restocking', 'Floor care and vacuuming'].map((item, i) => <li key={i} className="border-b border-gray-300 py-6 text-[#1a2744] text-lg md:text-xl font-medium first:pt-0" data-id="element-412">
                    {item}
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </section>

        {/* 4. Comprehensive Order Form Section */}
        <section className="w-full bg-white" id="quote-form" data-id="element-413">
          <div className="flex flex-col lg:flex-row w-full" data-id="element-414">
            {/* Left: Image with Yellow Background (Sticky) */}
            <div className="w-full lg:w-[35%] xl:w-[40%] bg-[#f5c518] relative min-h-[400px] lg:min-h-screen" data-id="element-415">
              <div className="lg:sticky lg:top-0 lg:h-screen w-full h-full overflow-hidden" data-id="element-416">
                <img src="https://cdn.magicpatterns.com/uploads/hsYfHybdP8AFrJp4GWQcRc/sj-website-commercial-img2.png" alt="Professional commercial cleaning" className="w-full h-full object-cover" loading="lazy" data-id="element-417" />
                <div className="absolute inset-0 bg-[#f5c518]/20 mix-blend-multiply" data-id="element-418" />
              </div>
            </div>

            {/* Right: Scrollable Form */}
            <div className="w-full lg:w-[65%] xl:w-[60%] px-6 py-16 md:px-16 lg:py-24" data-id="element-419">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.8
            }} className="max-w-3xl w-full mx-auto" data-id="element-420">
                <div className="mb-12" data-id="element-421">
                  <h2 className="font-bebas text-[#1a2744] leading-[0.85] tracking-wide mb-4" data-id="element-422">
                    <span className="text-5xl md:text-6xl lg:text-7xl block" data-id="element-423">
                      REQUEST A QUOTE
                    </span>
                    <span className="text-3xl md:text-4xl lg:text-5xl block text-[#e91e8c]" data-id="element-424">
                      IN 60 SECONDS
                    </span>
                  </h2>
                  <p className="text-gray-600 font-medium" data-id="element-425">
                    Fill in the questions below and we'll call you back
                    instantly with a tailored quote.
                  </p>
                </div>

                <form className="space-y-16" onSubmit={e => e.preventDefault()} data-id="element-426">
                  {/* STEP 1 */}
                  <div className="space-y-8" data-id="element-427">
                    <h3 className="font-bebas text-2xl md:text-3xl text-[#1a2744] border-b border-gray-200 pb-2" data-id="element-428">
                      <span className="text-[#e91e8c] mr-2" data-id="element-429">STEP 1:</span> Tell
                      us about your commercial space
                    </h3>

                    <div className="space-y-6" data-id="element-430">
                      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8" data-id="element-431">
                        <label className="text-[#1a2744] font-medium md:w-1/2" data-id="element-432">
                          What is the primary use of the space?
                        </label>
                        <div className="relative md:w-1/2" data-id="element-433">
                          <select value={primaryUse} onChange={e => setPrimaryUse(e.target.value)} className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors appearance-none cursor-pointer" data-id="element-434">
                            <option value="" disabled data-id="element-435">
                              Select space type
                            </option>
                            <option value="office" data-id="element-436">Office / Corporate</option>
                            <option value="retail" data-id="element-437">Retail Store</option>
                            <option value="medical" data-id="element-438">
                              Medical / Healthcare
                            </option>
                            <option value="educational" data-id="element-439">
                              Educational Facility
                            </option>
                            <option value="industrial" data-id="element-440">
                              Industrial / Warehouse
                            </option>
                            <option value="other" data-id="element-441">Other</option>
                          </select>
                          <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-442" />
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8" data-id="element-443">
                        <label className="text-[#1a2744] font-medium md:w-1/2" data-id="element-444">
                          Total Square Footage of entire area you want clean?
                        </label>
                        <input type="text" value={sqFootage} onChange={e => setSqFootage(e.target.value)} placeholder="Enter square footage" className="md:w-1/2 border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-445" />
                      </div>

                      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8" data-id="element-446">
                        <label className="text-[#1a2744] font-medium md:w-1/2" data-id="element-447">
                          Height of Ceilings
                        </label>
                        <input type="text" value={ceilings} onChange={e => setCeilings(e.target.value)} placeholder="Average height of ceiling" className="md:w-1/2 border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-448" />
                      </div>

                      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8" data-id="element-449">
                        <label className="text-[#1a2744] font-medium md:w-1/2" data-id="element-450">
                          Approx. Total # of all persons in the space?
                        </label>
                        <div className="relative md:w-1/2" data-id="element-451">
                          <select value={persons} onChange={e => setPersons(e.target.value)} className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors appearance-none cursor-pointer" data-id="element-452">
                            <option value="" disabled data-id="element-453">
                              Select range
                            </option>
                            <option value="1-10" data-id="element-454">1-10</option>
                            <option value="11-25" data-id="element-455">11-25</option>
                            <option value="26-50" data-id="element-456">26-50</option>
                            <option value="51-100" data-id="element-457">51-100</option>
                            <option value="100+" data-id="element-458">100+</option>
                          </select>
                          <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-459" />
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8" data-id="element-460">
                        <label className="text-[#1a2744] font-medium md:w-1/2" data-id="element-461">
                          At what frequency would you like to have the cleaning?
                        </label>
                        <div className="relative md:w-1/2" data-id="element-462">
                          <select value={frequency} onChange={e => setFrequency(e.target.value)} className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors appearance-none cursor-pointer" data-id="element-463">
                            <option value="" disabled data-id="element-464">
                              Select frequency
                            </option>
                            <option value="daily" data-id="element-465">Daily</option>
                            <option value="daily-weekends" data-id="element-466">
                              Daily Including weekends
                            </option>
                            <option value="weekly" data-id="element-467">Weekly</option>
                            <option value="biweekly" data-id="element-468">Bi-weekly</option>
                            <option value="monthly" data-id="element-469">Monthly</option>
                          </select>
                          <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-470" />
                        </div>
                      </div>

                      <div className="flex flex-col gap-3" data-id="element-471">
                        <label className="text-[#1a2744] font-medium" data-id="element-472">
                          What time of day would you like the space cleaned?
                        </label>
                        <div className="flex flex-wrap gap-3" data-id="element-473">
                          {['During Regular Business hours', 'Outside Regular Business hours', 'Overnight (11 pm to 7 am)'].map(time => <button key={time} type="button" onClick={() => setTimeOfDay(time)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${timeOfDay === time ? 'bg-[#e91e8c] border-[#e91e8c] text-white' : 'bg-transparent border-gray-300 text-gray-600 hover:border-[#e91e8c] hover:text-[#e91e8c]'}`} data-id="element-474">
                              {time}
                            </button>)}
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8 pt-2" data-id="element-475">
                        <label className="text-[#1a2744] font-medium md:w-2/3" data-id="element-476">
                          To your knowledge, has anyone in the space contracted
                          COVID-19 in the last month?
                        </label>
                        <div className="flex gap-3 md:w-1/3 md:justify-end" data-id="element-477">
                          <button type="button" onClick={() => setCovid(true)} className={`px-6 py-2 rounded-full text-sm font-medium transition-colors border ${covid === true ? 'bg-[#e91e8c] border-[#e91e8c] text-white' : 'bg-transparent border-gray-300 text-gray-600 hover:border-[#e91e8c] hover:text-[#e91e8c]'}`} data-id="element-478">
                            Yes
                          </button>
                          <button type="button" onClick={() => setCovid(false)} className={`px-6 py-2 rounded-full text-sm font-medium transition-colors border ${covid === false ? 'bg-[#1a2744] border-[#1a2744] text-white' : 'bg-transparent border-gray-300 text-gray-600 hover:border-[#1a2744] hover:text-[#1a2744]'}`} data-id="element-479">
                            No
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* STEP 2 */}
                  <div className="space-y-6" data-id="element-480">
                    <div data-id="element-481">
                      <h3 className="font-bebas text-2xl md:text-3xl text-[#1a2744] border-b border-gray-200 pb-2 mb-2" data-id="element-482">
                        <span className="text-[#e91e8c] mr-2" data-id="element-483">STEP 2:</span>{' '}
                        Select Space types
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-484">
                        Select the quantity for each space type in the area you
                        wish cleaned. This will help ensure we provide you the
                        most accurate estimate possible.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-485">
                      {/* Office Spaces */}
                      <div className="border border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center gap-4 hover:border-[#5bbcd5] transition-colors bg-gray-50/50" data-id="element-486">
                        <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center text-[#1a2744]" data-id="element-487">
                          <Building2 className="w-8 h-8" data-id="element-488" />
                        </div>
                        <span className="font-medium text-[#1a2744]" data-id="element-489">
                          Enclosed Offices Spaces
                        </span>
                        <div className="flex items-center gap-4 mt-2" data-id="element-490">
                          <button type="button" onClick={() => updateSpace('offices', false)} className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#e91e8c] hover:border-[#e91e8c] transition-colors" data-id="element-491">
                            <Minus className="w-4 h-4" data-id="element-492" />
                          </button>
                          <span className="w-8 text-xl font-semibold text-[#1a2744]" data-id="element-493">
                            {spaces.offices}
                          </span>
                          <button type="button" onClick={() => updateSpace('offices', true)} className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#e91e8c] hover:border-[#e91e8c] transition-colors" data-id="element-494">
                            <Plus className="w-4 h-4" data-id="element-495" />
                          </button>
                        </div>
                      </div>

                      {/* Conference Rooms */}
                      <div className="border border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center gap-4 hover:border-[#5bbcd5] transition-colors bg-gray-50/50" data-id="element-496">
                        <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center text-[#1a2744]" data-id="element-497">
                          <Users className="w-8 h-8" data-id="element-498" />
                        </div>
                        <span className="font-medium text-[#1a2744]" data-id="element-499">
                          Conference or Meeting rooms
                        </span>
                        <div className="flex items-center gap-4 mt-2" data-id="element-500">
                          <button type="button" onClick={() => updateSpace('conference', false)} className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#e91e8c] hover:border-[#e91e8c] transition-colors" data-id="element-501">
                            <Minus className="w-4 h-4" data-id="element-502" />
                          </button>
                          <span className="w-8 text-xl font-semibold text-[#1a2744]" data-id="element-503">
                            {spaces.conference}
                          </span>
                          <button type="button" onClick={() => updateSpace('conference', true)} className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#e91e8c] hover:border-[#e91e8c] transition-colors" data-id="element-504">
                            <Plus className="w-4 h-4" data-id="element-505" />
                          </button>
                        </div>
                      </div>

                      {/* Bathrooms */}
                      <div className="border border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center gap-4 hover:border-[#5bbcd5] transition-colors bg-gray-50/50" data-id="element-506">
                        <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center text-[#1a2744]" data-id="element-507">
                          <Droplets className="w-8 h-8" data-id="element-508" />
                        </div>
                        <span className="font-medium text-[#1a2744]" data-id="element-509">
                          Men & Women Bathrooms
                        </span>
                        <div className="flex items-center gap-4 mt-2" data-id="element-510">
                          <button type="button" onClick={() => updateSpace('bathrooms', false)} className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#e91e8c] hover:border-[#e91e8c] transition-colors" data-id="element-511">
                            <Minus className="w-4 h-4" data-id="element-512" />
                          </button>
                          <span className="w-8 text-xl font-semibold text-[#1a2744]" data-id="element-513">
                            {spaces.bathrooms}
                          </span>
                          <button type="button" onClick={() => updateSpace('bathrooms', true)} className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#e91e8c] hover:border-[#e91e8c] transition-colors" data-id="element-514">
                            <Plus className="w-4 h-4" data-id="element-515" />
                          </button>
                        </div>
                      </div>

                      {/* Cafeterias */}
                      <div className="border border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center gap-4 hover:border-[#5bbcd5] transition-colors bg-gray-50/50" data-id="element-516">
                        <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center text-[#1a2744]" data-id="element-517">
                          <Coffee className="w-8 h-8" data-id="element-518" />
                        </div>
                        <span className="font-medium text-[#1a2744]" data-id="element-519">
                          Cafeterias & Dining Areas
                        </span>
                        <div className="flex items-center gap-4 mt-2" data-id="element-520">
                          <button type="button" onClick={() => updateSpace('cafeterias', false)} className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#e91e8c] hover:border-[#e91e8c] transition-colors" data-id="element-521">
                            <Minus className="w-4 h-4" data-id="element-522" />
                          </button>
                          <span className="w-8 text-xl font-semibold text-[#1a2744]" data-id="element-523">
                            {spaces.cafeterias}
                          </span>
                          <button type="button" onClick={() => updateSpace('cafeterias', true)} className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#e91e8c] hover:border-[#e91e8c] transition-colors" data-id="element-524">
                            <Plus className="w-4 h-4" data-id="element-525" />
                          </button>
                        </div>
                      </div>

                      {/* Lounging */}
                      <div className="border border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center gap-4 hover:border-[#5bbcd5] transition-colors bg-gray-50/50 sm:col-span-2 md:col-span-1 md:col-start-1 md:col-end-3 lg:col-span-2 xl:col-span-1 xl:col-start-1 xl:col-end-3" data-id="element-526">
                        <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center text-[#1a2744]" data-id="element-527">
                          <Sofa className="w-8 h-8" data-id="element-528" />
                        </div>
                        <span className="font-medium text-[#1a2744]" data-id="element-529">
                          Lounging or Employee / Visitors areas
                        </span>
                        <div className="flex items-center gap-4 mt-2" data-id="element-530">
                          <button type="button" onClick={() => updateSpace('lounging', false)} className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#e91e8c] hover:border-[#e91e8c] transition-colors" data-id="element-531">
                            <Minus className="w-4 h-4" data-id="element-532" />
                          </button>
                          <span className="w-8 text-xl font-semibold text-[#1a2744]" data-id="element-533">
                            {spaces.lounging}
                          </span>
                          <button type="button" onClick={() => updateSpace('lounging', true)} className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#e91e8c] hover:border-[#e91e8c] transition-colors" data-id="element-534">
                            <Plus className="w-4 h-4" data-id="element-535" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* STEP 3 */}
                  <div className="space-y-6" data-id="element-536">
                    <div data-id="element-537">
                      <h3 className="font-bebas text-2xl md:text-3xl text-[#1a2744] border-b border-gray-200 pb-2 mb-2" data-id="element-538">
                        <span className="text-[#e91e8c] mr-2" data-id="element-539">STEP 3:</span>{' '}
                        Type of Flooring
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-540">
                        Provide your best assessment of the overall flooring
                        type in the area you would like cleaned
                      </p>
                    </div>

                    <div className="space-y-4" data-id="element-541">
                      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-8" data-id="element-542">
                        <label className="text-[#1a2744] font-medium md:w-2/3" data-id="element-543">
                          Wood - Approximate % of the total floor space?
                        </label>
                        <div className="relative md:w-1/3" data-id="element-544">
                          <select value={flooring.wood} onChange={e => setFlooring({
                          ...flooring,
                          wood: e.target.value
                        })} className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors appearance-none cursor-pointer" data-id="element-545">
                            <option value="" disabled data-id="element-546">
                              Select %
                            </option>
                            {percentages.map(p => <option key={p} value={p} data-id="element-547">
                                {p}
                              </option>)}
                          </select>
                          <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-548" />
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-8" data-id="element-549">
                        <label className="text-[#1a2744] font-medium md:w-2/3" data-id="element-550">
                          Cement - Approximate % of the floor space?
                        </label>
                        <div className="relative md:w-1/3" data-id="element-551">
                          <select value={flooring.cement} onChange={e => setFlooring({
                          ...flooring,
                          cement: e.target.value
                        })} className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors appearance-none cursor-pointer" data-id="element-552">
                            <option value="" disabled data-id="element-553">
                              Select %
                            </option>
                            {percentages.map(p => <option key={p} value={p} data-id="element-554">
                                {p}
                              </option>)}
                          </select>
                          <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-555" />
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-8" data-id="element-556">
                        <label className="text-[#1a2744] font-medium md:w-2/3" data-id="element-557">
                          Carpeting - Approximate % of the floor space?
                          (excludes area & moveable rugs)
                        </label>
                        <div className="relative md:w-1/3" data-id="element-558">
                          <select value={flooring.carpeting} onChange={e => setFlooring({
                          ...flooring,
                          carpeting: e.target.value
                        })} className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors appearance-none cursor-pointer" data-id="element-559">
                            <option value="" disabled data-id="element-560">
                              Select %
                            </option>
                            {percentages.map(p => <option key={p} value={p} data-id="element-561">
                                {p}
                              </option>)}
                          </select>
                          <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-562" />
                        </div>
                      </div>

                      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-8" data-id="element-563">
                        <label className="text-[#1a2744] font-medium md:w-2/3" data-id="element-564">
                          Ceramic/Marble/Finished stone - Approximate % of the
                          floor space?
                        </label>
                        <div className="relative md:w-1/3" data-id="element-565">
                          <select value={flooring.ceramic} onChange={e => setFlooring({
                          ...flooring,
                          ceramic: e.target.value
                        })} className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors appearance-none cursor-pointer" data-id="element-566">
                            <option value="" disabled data-id="element-567">
                              Select %
                            </option>
                            {percentages.map(p => <option key={p} value={p} data-id="element-568">
                                {p}
                              </option>)}
                          </select>
                          <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-569" />
                        </div>
                      </div>

                      <div className="pt-4" data-id="element-570">
                        <label className="block text-[#1a2744] font-medium mb-2" data-id="element-571">
                          Is there any other information regarding the floor
                          space?
                        </label>
                        <textarea value={flooring.other} onChange={e => setFlooring({
                        ...flooring,
                        other: e.target.value
                      })} placeholder="Please add any other information you feel we should know about and is pertinent or missing in the above." rows={3} className="w-full border border-gray-300 rounded-sm p-4 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] focus:ring-1 focus:ring-[#e91e8c] transition-all resize-none placeholder-gray-400" data-id="element-572"></textarea>
                      </div>
                    </div>
                  </div>

                  {/* STEP 4 */}
                  <div className="space-y-6" data-id="element-573">
                    <div data-id="element-574">
                      <h3 className="font-bebas text-2xl md:text-3xl text-[#1a2744] border-b border-gray-200 pb-2 mb-2" data-id="element-575">
                        <span className="text-[#e91e8c] mr-2" data-id="element-576">STEP 4:</span> Let
                        us know who we'll be contacting?
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-577">
                        You will instantly receive an email summarizing the
                        information entered in this Quote Request. In addition,
                        we will call you within the next 10 minutes to provide
                        you a quote.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-578">
                      <input type="text" name="firstName" value={contact.firstName} onChange={handleContactChange} placeholder="First Name" className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-579" />
                      <input type="text" name="lastName" value={contact.lastName} onChange={handleContactChange} placeholder="Last Name" className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-580" />
                      <input type="email" name="email" value={contact.email} onChange={handleContactChange} placeholder="Email Address" className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-581" />
                      <input type="tel" name="phone" value={contact.phone} onChange={handleContactChange} placeholder="Phone Number" className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-582" />
                    </div>
                  </div>

                  {/* STEP 5 */}
                  <div className="space-y-6" data-id="element-583">
                    <h3 className="font-bebas text-2xl md:text-3xl text-[#1a2744] border-b border-gray-200 pb-2" data-id="element-584">
                      <span className="text-[#e91e8c] mr-2" data-id="element-585">STEP 5:</span>{' '}
                      Location of the commercial space
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-586">
                      <input type="text" name="location" value={contact.location} onChange={handleContactChange} placeholder="Enter a location / Street Address" className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-587" />
                      <input type="text" name="unit" value={contact.unit} onChange={handleContactChange} placeholder="Unit/Apt/Suite #" className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-588" />
                      <input type="text" name="city" value={contact.city} onChange={handleContactChange} placeholder="City" className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-589" />
                      <input type="text" name="postalCode" value={contact.postalCode} onChange={handleContactChange} placeholder="Postal Code" className="w-full border-b border-gray-300 py-2 bg-transparent text-[#1a2744] focus:outline-none focus:border-[#e91e8c] transition-colors placeholder-gray-400" data-id="element-590" />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-center pt-8 pb-4" data-id="element-591">
                    <button type="submit" className="bg-[#e91e8c] hover:bg-[#d0187c] text-white px-12 py-4 rounded-full font-semibold tracking-wider text-lg transition-colors shadow-lg" data-id="element-592">
                      REQUEST A QUOTE
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          </div>
        </section>

        {/* 5. CTA Section */}
        <section className="w-full bg-[#f8f9fa] py-16 px-6 md:px-12" data-id="element-593">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 text-center md:text-left" data-id="element-594">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2744] leading-tight" data-id="element-595">
              Click to set up Commercial Cleaning
            </h2>
            <button onClick={() => {
            const el = document.getElementById('quote-form');
            if (el) el.scrollIntoView({
              behavior: 'smooth'
            });
          }} className="bg-[#e91e8c] hover:bg-[#d0187c] text-white px-8 py-4 rounded-full font-semibold tracking-wide transition-colors shadow-md flex items-center gap-3 whitespace-nowrap" data-id="element-596">
              Book Online <span className="text-xl leading-none" data-id="element-597">&rarr;</span>
            </button>
          </motion.div>
        </section>
      </main>

      <Footer data-id="element-598" />
    </div>;
}